// The canvas's left rail: which source a tile reads, and every field in it.
//
// The three groups are the source's own `role` vocabulary (`time`, `dimension`,
// `measure`), derived warehouse-side from the BigQuery type (`bi/core/sources.py`).
// Grouping by role answers "what can I measure" and "what can I break this down
// by" as two lists.
//
// **The description is INLINE, not on hover.** It is the only place a column's
// meaning is written, and in a tooltip nobody read it. The filter box above the
// groups keeps the wall navigable.
//
// **The undocumented marker is not decoration.** A studio source is DISCOVERED,
// not authored, so an undescribed column cannot be picked correctly. Hiding it
// would move the guess, not remove it. Surfacing the gap gets it documented.
//
// **A source with no rows for the scoped experiment says so in the picker.** The
// register knows this without a query (`bi/core/experiments.py`), and an empty
// funnel under an experiment's name reads as a flat funnel.
import { useMemo, useState } from "react";

import type { BiSource, BiSourceColumn, BiSourceSummary, SourceRole } from "@bi/shared/types";

import { InfoIcon } from "../icons";

export interface FieldRailProps {
  sources: readonly BiSourceSummary[];
  /** `null` while the source list is still loading, or when the selected tile
   *  is a text tile with no source of its own. */
  source: BiSource | null;
  selectedSourceId: string;
  onSelectSource: (sourceId: string) => void;
  /** Apply a field to the selected tile. The rail proposes; the property panel
   *  decides what "apply" means for the tile that is selected — a measure
   *  becomes a metric, a dimension a breakdown, a time column the axis. */
  onUseField?: (column: BiSourceColumn) => void;
  /** How the source list arrived. An empty list with no timestamp is "the
   *  studio views have not shipped to this environment yet", which is a
   *  different sentence from "there is nothing to build over". */
  syncedAt?: string | null;
  /** The experiment this canvas is scoped to, or `null`. */
  experimentKey?: string | null;
  /** Source ids the register says carry rows for `experimentKey`, or `null`
   *  when the register is not known yet. `null` and `[]` are different answers
   *  and the rail renders them differently: unknown is silent, known-and-empty
   *  is a warning. */
  coveredSourceIds?: readonly string[] | null;
  disabled?: boolean;
}

const GROUP_ORDER: SourceRole[] = ["time", "dimension", "measure"];

const GROUP_LABELS: Record<SourceRole, string> = {
  time: "Time",
  dimension: "Dimensions",
  measure: "Measures",
};

/** Case-insensitive, and `_`/`-` read as spaces so "delta vs default" finds
 *  `delta_vs_default`. Mirrors `bi/core/search.py::normalise` — the same query
 *  should behave the same way in the rail and in the search box. */
function matches(column: BiSourceColumn, query: string): boolean {
  const needle = query.trim().toLowerCase().replace(/[_\-.]+/g, " ");
  if (!needle) return true;
  const haystack = `${column.name} ${column.description ?? ""}`
    .toLowerCase()
    .replace(/[_\-.]+/g, " ");
  return needle.split(/\s+/).every((term) => haystack.includes(term));
}

export function FieldRail({
  sources,
  source,
  selectedSourceId,
  onSelectSource,
  onUseField,
  syncedAt,
  experimentKey = null,
  coveredSourceIds = null,
  disabled = false,
}: FieldRailProps) {
  const [query, setQuery] = useState("");
  const columns = useMemo(() => source?.columns ?? [], [source]);
  const undocumented = columns.filter((c) => !c.documented).length;
  const visible = useMemo(() => columns.filter((c) => matches(c, query)), [columns, query]);

  const covers = (sourceId: string): boolean | null =>
    coveredSourceIds === null || coveredSourceIds.length === 0
      ? null
      : coveredSourceIds.includes(sourceId);
  const selectedCovers = selectedSourceId ? covers(selectedSourceId) : null;

  return (
    <aside className="bi-studio-rail" aria-label="Fields">
      <label className="bi-studio-rail__picker">
        <span className="bi-filter__label">Data source</span>
        <select
          className="bi-studio-select"
          value={selectedSourceId}
          disabled={disabled || sources.length === 0}
          onChange={(e) => onSelectSource(e.target.value)}
        >
          <option value="">Pick a source</option>
          {sources.map((s) => {
            const carries = covers(s.id);
            return (
              <option key={s.id} value={s.id}>
                {s.title}
                {carries === false ? " (no rows for this experiment)" : ""}
              </option>
            );
          })}
        </select>
      </label>

      {sources.length === 0 ? (
        <p className="bi-studio-rail__note">
          {syncedAt
            ? "This environment has no studio views to build over."
            : "The studio views have not reached this environment's warehouse yet."}
        </p>
      ) : null}

      {selectedCovers === false && experimentKey ? (
        <p className="bi-studio-rail__warn" role="status">
          This source carries no rows for {experimentKey}. A tile built over it will draw a
          blank, and a blank under one experiment's name reads as a result. Pick one of:{" "}
          {(coveredSourceIds ?? []).join(", ")}.
        </p>
      ) : null}

      {source?.description ? (
        <p className="bi-studio-rail__note">{source.description}</p>
      ) : null}

      {undocumented > 0 ? (
        <p className="bi-studio-rail__note">
          {undocumented} of {columns.length} field{columns.length === 1 ? "" : "s"} carry no
          description in the warehouse. They are marked below; a chart built on a guess about
          what one means is a wrong number with a title.
        </p>
      ) : null}

      {columns.length > 0 ? (
        <label className="bi-studio-rail__picker">
          <span className="bi-filter__label">Find a field</span>
          <input
            className="bi-studio-select"
            type="search"
            value={query}
            placeholder="name or description"
            disabled={disabled}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
      ) : null}

      {query && visible.length === 0 ? (
        <p className="bi-studio-rail__note">
          No field on {source?.title ?? "this source"} matches that. The search box in the
          header looks across every source.
        </p>
      ) : null}

      {GROUP_ORDER.map((role) => {
        const group = visible.filter((c) => c.role === role);
        if (group.length === 0) return null;
        return (
          <section key={role} className="bi-studio-rail__group">
            <h3 className="bi-studio-rail__group-title">{GROUP_LABELS[role]}</h3>
            <ul className="bi-studio-rail__fields">
              {group.map((column) => (
                <li key={column.name}>
                  <FieldChip column={column} onUse={onUseField} disabled={disabled} />
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </aside>
  );
}

/** One field: its name, its type, and the sentence the warehouse wrote about
 *  it. The whole row is the button, so applying a field is one click wherever
 *  the pointer happens to be inside it. */
export function FieldChip({
  column,
  onUse,
  disabled = false,
}: {
  column: BiSourceColumn;
  onUse?: (column: BiSourceColumn) => void;
  disabled?: boolean;
}) {
  const described = column.documented && column.description;
  const detail = described
    ? column.description
    : "No description in the warehouse, so what this column means is a guess.";
  return (
    <button
      type="button"
      className={`bi-studio-field-row bi-studio-field-row--${column.role}`}
      disabled={disabled || !onUse}
      aria-label={`${column.name}. ${column.bq_type}. ${detail}`}
      onClick={() => onUse?.(column)}
    >
      <span className="bi-studio-field-row__head">
        <span className="bi-studio-chip__name">{column.name}</span>
        <span className="bi-studio-field-row__type">{column.bq_type}</span>
        {described ? null : (
          <span className="bi-studio-chip__undocumented" aria-hidden="true">
            <InfoIcon size={12} />
          </span>
        )}
      </span>
      <span
        className={`bi-studio-field-row__detail${
          described ? "" : " bi-studio-field-row__detail--missing"
        }`}
      >
        {detail}
      </span>
    </button>
  );
}
